// src/components/pages/admin-dash/ContactMessages.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";

/**
 * NEW: Contact-form inbox.
 *
 * Surfaces submissions to /site/contact-message (the public Contact form
 * used by the Settings → Support tab and the landing-page contact
 * section). Filters by status, supports mark-read / archive / reply
 * actions.
 *
 * Backend endpoints (ContactMessageAdminResource, /admin/contact-message):
 *   GET  /admin/contact-message?status=NEW|READ|ANSWERED|ARCHIVED
 *   PUT  /admin/contact-message/{id}      { status: "NEW"|"READ"|"ANSWERED"|"ARCHIVED" }
 *
 * Status wire values match the backend's StatusContact enum — a message
 * that hasn't been opened yet is "NEW" there, displayed as "Unread" here.
 *
 * Props:
 *   onShowToast(message, tone)
 */
const TABS = [
  { id: "NEW",      label: "Unread",   icon: "mail" },
  { id: "READ",     label: "Read",     icon: "mail-opened" },
  { id: "ARCHIVED", label: "Archived", icon: "archive" },
];

const relTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function ContactMessages({ onShowToast }) {
  const [status, setStatus]     = useState("NEW");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  const fetchMessages = async (s) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/admin/contact-message", {
        params: { status: s },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      data.sort((a, b) =>
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setMessages(data);
      // Auto-select first message in the list
      if (data.length > 0) setSelectedId(data[0].id);
      else setSelectedId(null);
    } catch {
      setMessages([]);
      onShowToast?.("Couldn't load contact messages.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMessages(status); }, [status]); // eslint-disable-line

  const selected = useMemo(
    () => messages.find((m) => m.id === selectedId) || null,
    [messages, selectedId]
  );

  const updateStatus = async (id, next) => {
    try {
      await axiosInstance.put(`/admin/contact-message/${id}`, { status: next });
      onShowToast?.(
        next === "ARCHIVED" ? "Message archived." :
        next === "READ"     ? "Marked as read." :
                              "Marked unread.",
        "success"
      );
      fetchMessages(status);
    } catch {
      onShowToast?.("Couldn't update message.", "error");
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
            Contact inbox
          </h1>
          <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
            Messages submitted through the support form.
          </p>
        </div>
        <Button icon="refresh" size="sm" variant="ghost" onClick={() => fetchMessages(status)} loading={loading}>Refresh
        </Button>
      </header>

      {/* Tabs */}
      <div
        className="flex flex-wrap gap-1 p-1 rounded-full w-fit"
        style={{ background: "var(--surf-2)", border: "1px solid var(--border)" }}
      >
        {TABS.map((t) => {
          const active = status === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatus(t.id)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium transition-colors"
              style={{
                background: active ? "var(--gold)" : "transparent",
                color:      active ? "#0E2A18"     : "var(--txt-2)",
              }}
            >
              <Icon name={t.icon} size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Split-pane: list left, detail right */}
      <div className="grid gap-3 lg:grid-cols-[320px_1fr]">
        {/* List */}
        <Card variant="soft" className="!p-0 overflow-hidden">
          {loading ? (
            <div className="px-4 py-10 text-center">
              <Spinner size={16} />
              <div className="text-[12px] mt-2" style={{ color: "var(--txt-2)" }}>
                Loading messages…
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Icon name="mail" size={26} style={{ color: "var(--txt-3)" }} />
              <div className="text-[13px] mt-2" style={{ color: "var(--txt-2)" }}>
                No {(TABS.find((t) => t.id === status)?.label || status).toLowerCase()} messages.
              </div>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {messages.map((m) => (
                <MessageRow
                  key={m.id}
                  m={m}
                  active={selectedId === m.id}
                  onClick={() => setSelectedId(m.id)}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Detail */}
        <div>
          {selected ? (
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="min-w-0 flex-1">
                  <h2
                    className="font-semibold leading-tight mb-1 truncate"
                    style={{ fontSize: 18, color: "var(--txt)" }}
                  >
                    {selected.subject || "(no subject)"}
                  </h2>
                  <div className="text-[12px]" style={{ color: "var(--txt-2)" }}>
                    From{" "}
                    <span style={{ color: "var(--txt)" }}>
                      {selected.name || "Anonymous"}
                    </span>{" "}
                    <a
                      href={`mailto:${selected.email}`}
                      className="hover:underline"
                      style={{ color: "var(--gold)" }}
                    >
                      &lt;{selected.email}&gt;
                    </a>{" "}
                    · {relTime(selected.createdAt)}
                  </div>
                </div>
                <StatusPill status={selected.status} />
              </div>

              <div
                className="rounded-xl p-4 text-[13.5px] whitespace-pre-wrap leading-relaxed"
                style={{
                  background: "var(--surf-2)",
                  color: "var(--txt)",
                  border: "1px solid var(--border)",
                }}
              >
                {selected.message || "(empty)"}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2 mt-4">
                {selected.status !== "ARCHIVED" && (
                  <Button icon="archive"
                    size="sm"
                    variant="ghost"
                    onClick={() => updateStatus(selected.id, "ARCHIVED")}>Archive
                  </Button>
                )}
                {selected.status === "NEW" && (
                  <Button icon="check"
                    size="sm"
                    variant="ghost"
                    onClick={() => updateStatus(selected.id, "READ")}>Mark read
                  </Button>
                )}
                {selected.status === "READ" && (
                  <Button icon="mail"
                    size="sm"
                    variant="ghost"
                    onClick={() => updateStatus(selected.id, "NEW")}>Mark unread
                  </Button>
                )}
                <Button icon="send"
                  size="sm"
                  onClick={() =>
                    window.open(
                      `mailto:${selected.email}?subject=Re: ${encodeURIComponent(
                        selected.subject || ""
                      )}`,
                      "_blank"
                    )
                  }>Reply
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="text-center py-10">
                <Icon name="mail" size={32} style={{ color: "var(--txt-3)" }} />
                <p className="text-[13px] mt-2" style={{ color: "var(--txt-2)" }}>
                  Select a message to read.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageRow({ m, active, onClick }) {
  const isUnread = m.status === "NEW";
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-4 py-3 transition-colors"
      style={{
        background: active ? "var(--gold-dim)" : "transparent",
        borderLeft: active
          ? "3px solid var(--gold)"
          : "3px solid transparent",
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = "var(--surf-3)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      <div className="flex items-center gap-2 mb-0.5">
        {isUnread && (
          <span
            className="inline-block rounded-full"
            style={{
              width: 6, height: 6,
              background: "var(--gold)",
              flexShrink: 0,
            }}
          />
        )}
        <span
          className="font-medium text-[13px] truncate"
          style={{
            color: "var(--txt)",
            fontWeight: isUnread ? 600 : 500,
          }}
        >
          {m.name || m.email || "Anonymous"}
        </span>
        <span
          className="ml-auto text-[10px] tabular-nums shrink-0"
          style={{ color: "var(--txt-3)" }}
        >
          {relTime(m.createdAt)}
        </span>
      </div>
      <div
        className="text-[12px] truncate"
        style={{ color: "var(--txt-2)" }}
      >
        {m.subject || "(no subject)"}
      </div>
      <div
        className="text-[11px] truncate mt-0.5"
        style={{ color: "var(--txt-3)" }}
      >
        {(m.message || "").slice(0, 100)}
      </div>
    </button>
  );
}

function StatusPill({ status }) {
  if (status === "ARCHIVED") return <Badge variant="neutral">Archived</Badge>;
  if (status === "READ")     return <Badge variant="teal">Read</Badge>;
  return <Badge variant="gold">Unread</Badge>;
}
